import { useQueryClient } from '@tanstack/react-query';
import { NewOutlineButton, OutlineButton, PrimaryButton } from 'Components/Buttons';
import { useState } from 'preact/hooks';
import { useDispatch } from 'react-redux';
import useFetch from 'use-http';

import ActionModal from '@/components/ActionModal';
import LoaderModal from '@/components/LoaderModal';
import ErrorModal from '@/components/modals/Error';
import SuccessModal from '@/components/modals/Success';
import { addInterestedToCachedEvent } from '@/queries/events';
import { useSelector } from '@/redux/helper';
import { selectIsRemindedOfEvent } from '@/redux/selectors/users';
import { setUserData } from '@/redux/slices/user/actions';
import { buildURL } from '@/shared/fetch';
import { logGAEvent } from '@/shared/ga';

import { notificationPermissionStatus, requestNotificationPermission } from '../../helpers';
import AddToCalendarButton from './AddToCalendarButton';

const TalkReminderModal = ({
  id,
  eventTitle,
  eventDescription,
  startTime,
  stopTime,
  animal,
  url,
  businessHourId,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  // Read before the request goes out: the `setUserData` below flips this to true, but `onAllow` has
  // already captured the value from the render it ran in.
  const remindedBefore = useSelector(selectIsRemindedOfEvent(id));
  const [error, setError] = useState();
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const {
    post,
    response: reminderResponse,
    loading: sendingReminder,
  } = useFetch(buildURL(), { credentials: 'include', cachePolicy: 'no-cache' });

  const logUserInteraction = (action) => {
    logGAEvent(action, animal, businessHourId);
  };

  const onAllow = async () => {
    const permissionState = await notificationPermissionStatus();
    const granted = permissionState === 'granted';
    const denied = permissionState === 'denied';
    if (!granted && !denied) {
      requestNotificationPermission();
    }
    // The endpoint resends the calendar invite on every call but stores the reminder only once, so a
    // viewer who is already reminded must not be counted again. The response can't tell us — it
    // carries the entry either way, added now or on an earlier call — hence `remindedBefore`.
    const data = await post('/schedules/reminder', { scheduleId: id });
    if (reminderResponse.ok) {
      setShowSuccessModal(true);
      // Keeps `scheduledEvents` current, so a second Remind Me in this session sees the entry above.
      if (data?.user) {
        dispatch(setUserData(data.user));
      }
      // The viewer is now one of the interested; show that on the card behind this modal without
      // waiting for a refetch.
      if (!remindedBefore) {
        addInterestedToCachedEvent(queryClient, id);
      }
    } else {
      setError('Something went wrong. Please try again');
    }
    logUserInteraction('Click-Scheduled-Talk-Reminder');
  };

  return (
    <>
      {!error && !showSuccessModal && (
        <ActionModal
          onClose={onClose}
          title="Set Event Reminder"
          subTitle="Want to try to join us for this event?"
          primaryButton={<PrimaryButton size="large" label="Remind Me" onClick={onAllow} className="w-full" />}
          secondaryButton={<NewOutlineButton size="large" label="Never Mind" onClick={onClose} className="w-full" />}
        />
      )}
      {sendingReminder && <LoaderModal />}
      {error && <ErrorModal text={error} onClose={onClose} />}
      {showSuccessModal && (
        <SuccessModal
          text="You will receive a notification on this device when the event starts. We also emailed you a calendar invite with all the details!"
          onClose={onClose}
          extraButtons={
            <>
              <div className="mt-6">
                <AddToCalendarButton
                  title={eventTitle}
                  description={eventDescription}
                  startTime={startTime}
                  stopTime={stopTime}
                  url={url}
                />
              </div>
              <div className="mt-6">
                <OutlineButton size="large" label="Close" onClick={onClose} />
              </div>
            </>
          }
        />
      )}
    </>
  );
};

export default TalkReminderModal;
